import { useEffect, useMemo, useState } from 'react'
import { Zap, Loader2, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useFlows } from '@/hooks/use-flows'
import { useWorkspace } from '@/providers/workspace-provider'
import { flowMatchesWorkspaceScope } from '@/lib/flow-workspace-scope'
import { openSidebarMode } from '@/lib/sidebar-mode'
import { cn } from '@/lib/utils'
import { formatUserFacingStoragePath } from '@/lib/app-routes'
import {
  HomeFeedList,
  HomeFeedRow,
  HomeWidgetShell,
} from '@/components/dashboard/home-widget-shell'
import { HomeFeedContextMenu } from '@/components/dashboard/home-feed-context-menu'
import {
  flowToSelection,
  homeSelectionKey,
  type HomeContinuityProps,
} from '@/components/dashboard/home-selection'
import { testIds } from '@/lib/test-ids'
import type { Flow } from '@/lib/api'

const FEED_LIMIT = 5

function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'Never run'
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  const h = Math.floor((Date.now() - t) / 3600000)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 14) return `${d}d ago`
  return new Date(iso).toLocaleDateString()
}

function statusTone(status: string | null): string {
  if (status === 'completed') return 'text-green-600'
  if (status === 'failed') return 'text-destructive'
  if (status === 'running') return 'text-blue-600'
  return 'text-muted-foreground'
}

interface HomeFlowsFeedSectionProps {
  onFlowClick?: (flow: Flow) => void
  continuity?: HomeContinuityProps
}

export function HomeFlowsFeedSection({ onFlowClick, continuity }: HomeFlowsFeedSectionProps) {
  const { flows, loading, error } = useFlows()
  const { activeFolderPaths } = useWorkspace()
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    flow: Flow
  } | null>(null)

  const feedFlows = useMemo(() => {
    return flows
      .filter((flow) => flowMatchesWorkspaceScope(flow.file_path, activeFolderPaths))
      .sort((a, b) => {
        const ta = a.last_run_at ? new Date(a.last_run_at).getTime() : 0
        const tb = b.last_run_at ? new Date(b.last_run_at).getTime() : 0
        return tb - ta
      })
      .slice(0, FEED_LIMIT)
  }, [activeFolderPaths, flows])

  useEffect(() => {
    if (!contextMenu) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null)
    }
    const handleClick = () => setContextMenu(null)
    document.addEventListener('keydown', handleKey)
    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.removeEventListener('mousedown', handleClick)
    }
  }, [contextMenu])

  const openFlowsSidebar = () => openSidebarMode('flows')

  const selectFlow = (flow: Flow, opts?: { openPreview?: boolean }) => {
    continuity?.selectHome(flowToSelection(flow), { ...opts, flow })
  }

  const openFlow = (flow: Flow) => {
    if (continuity) {
      continuity.selectHome(flowToSelection(flow), { flow })
      continuity.openSelection(flowToSelection(flow))
      return
    }
    if (onFlowClick) {
      onFlowClick(flow)
      return
    }
    openFlowsSidebar()
  }

  return (
    <HomeWidgetShell
      title="Flows"
      icon={<Zap />}
      data-testid={testIds.home.flowsFeed}
      className="min-h-[180px]"
      footer={
        <Button variant="ghost" size="sm" className="w-full h-8" onClick={openFlowsSidebar}>
          Open Flows in sidebar
          <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      }
    >
      {contextMenu && (
        <HomeFeedContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onOpen={() => openFlow(contextMenu.flow)}
          onPreview={() => selectFlow(contextMenu.flow, { openPreview: true })}
          filePath={contextMenu.flow.file_path}
          workspaces={continuity?.workspaces ?? []}
        />
      )}

      {loading && (
        <div className="flex flex-1 items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && !loading && (
        <p className="text-sm text-muted-foreground py-4">Could not load flows</p>
      )}

      {!loading && !error && feedFlows.length === 0 && (
        <p className="text-sm text-muted-foreground py-6 text-center flex-1">No flows yet</p>
      )}

      {!loading && !error && feedFlows.length > 0 && (
        <HomeFeedList>
          {feedFlows.map((flow) => {
            const isSelected =
              continuity != null &&
              homeSelectionKey(flowToSelection(flow)) === continuity.selectedKey
            const tooltip = flow.file_path
              ? formatUserFacingStoragePath(flow.file_path, continuity?.workspaces ?? [])
              : undefined
            return (
              <HomeFeedRow
                key={flow.id}
                title={flow.title}
                meta={`Last run ${formatRelativeTime(flow.last_run_at)}`}
                trailing={
                  <span className={cn('capitalize', statusTone(flow.last_run_status))}>
                    {flow.last_run_status ?? (flow.enabled ? 'idle' : 'off')}
                  </span>
                }
                selected={isSelected}
                disabled={!flow.enabled}
                titleAttr={tooltip}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey) {
                    selectFlow(flow)
                    openFlow(flow)
                    return
                  }
                  selectFlow(flow)
                }}
                onDoubleClick={() => {
                  selectFlow(flow)
                  openFlow(flow)
                }}
                onContextMenu={(e) => {
                  e.preventDefault()
                  selectFlow(flow)
                  setContextMenu({ x: e.clientX, y: e.clientY, flow })
                }}
              />
            )
          })}
        </HomeFeedList>
      )}
    </HomeWidgetShell>
  )
}
